/**
 * Basic section — emoji button (inline with name input) + emoji picker popup, category selector.
 */

import React, { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { EmojiClickData } from 'emoji-picker-react'
import { useCategories } from '@turnifly/shared/hooks'
import { Input } from '@turnifly/shared/ui'

const EmojiPicker = lazy(() =>
  import('emoji-picker-react').catch(() => {
    // Chunk hash mismatch after deploy — reload page to get fresh assets
    window.location.reload()
    return { default: () => null } as never
  }),
)

/* Fallback categories if DB is empty — synced with migration 015 + additions */
const FALLBACK_CATEGORIES = [
  { id: 'family', icon: '👨‍👩‍👧‍👦', name_uz: 'Oila', name_en: 'Family' },
  { id: 'work', icon: '💼', name_uz: 'Ish', name_en: 'Work' },
  { id: 'sport', icon: '💪', name_uz: 'Sport', name_en: 'Sport' },
  { id: 'education', icon: '📚', name_uz: 'Ta\'lim', name_en: 'Education' },
  { id: 'spiritual', icon: '🕌', name_uz: 'Ruhiy', name_en: 'Spiritual' },
  { id: 'business', icon: '💰', name_uz: 'Biznes', name_en: 'Business' },
  { id: 'health', icon: '❤️', name_uz: 'Sog\'liq', name_en: 'Health' },
  { id: 'creativity', icon: '🎨', name_uz: 'Ijodkorlik', name_en: 'Creativity' },
  { id: 'mental', icon: '🧠', name_uz: 'Aqliy', name_en: 'Mental' },
  { id: 'tech', icon: '💻', name_uz: 'IT', name_en: 'IT' },
  { id: 'language', icon: '🌐', name_uz: 'Til o\'rganish', name_en: 'Language' },
  { id: 'lifestyle', icon: '🌱', name_uz: 'Turmush tarzi', name_en: 'Lifestyle' },
  { id: 'personal_growth', icon: '🚀', name_uz: 'Shaxsiy rivojlanish', name_en: 'Personal Growth' },
  { id: 'finance', icon: '💵', name_uz: 'Moliya', name_en: 'Finance' },
  { id: 'social', icon: '🤝', name_uz: 'Ijtimoiy munosabatlar', name_en: 'Social Relations' },
  { id: 'sleep', icon: '😴', name_uz: 'Uyqu', name_en: 'Sleep' },
  { id: 'rest', icon: '🏖️', name_uz: 'Dam olish', name_en: 'Rest' },
  { id: 'tidiness', icon: '🧹', name_uz: 'Tartib (Tozalik)', name_en: 'Tidiness' },
]

interface HabitFormBasicProps {
  icon: string
  name: string
  category: string
  onIconChange: (v: string) => void
  onNameChange: (v: string) => void
  onCategoryChange: (v: string) => void
}

export const HabitFormBasic: React.FC<HabitFormBasicProps> = ({
  icon, name, category, onIconChange, onNameChange, onCategoryChange,
}) => {
  const { t, i18n } = useTranslation()
  const { data: categories } = useCategories()
  const dbCats = (categories ?? []) as Array<Record<string, unknown>>
  const catList = dbCats.length > 0 ? dbCats : FALLBACK_CATEGORIES
  const [isPickerOpen, setPickerOpen] = useState(false)
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 })
  const pickerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleEmojiSelect = (emojiData: EmojiClickData): void => {
    onIconChange(emojiData.emoji)
    setPickerOpen(false)
  }

  const handleNameChange = (val: string): void => {
    if (val.length <= 40) onNameChange(val)
  }

  const openPicker = useCallback((): void => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const pickerW = 320
      const pickerH = 400
      // Horizontal: align left edge with button, clamp to viewport
      let left = rect.left
      if (left + pickerW > window.innerWidth - 8) {
        left = window.innerWidth - pickerW - 8
      }
      if (left < 8) left = 8
      // Vertical: prefer below button, flip above if no space
      let top = rect.bottom + 6
      if (top + pickerH > window.innerHeight - 8) {
        top = rect.top - pickerH - 6
      }
      if (top < 8) top = 8
      setPickerPos({ top, left })
    }
    setPickerOpen((p) => !p)
  }, [])

  // Close picker on outside click
  useEffect(() => {
    if (!isPickerOpen) return

    const handleClickOutside = (e: MouseEvent | TouchEvent): void => {
      const target = e.target as Node
      if (
        pickerRef.current && !pickerRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setPickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isPickerOpen])

  return (
    <>
      {/* Icon & Name */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-2">
          {t('home.habitForm.icon')} & {t('home.habitForm.name')}
        </label>
        <div className="relative flex items-center gap-2">
          {/* Emoji button — shows selected icon or + when default */}
          <div className="relative">
            <button
              ref={buttonRef}
              type="button"
              onClick={openPicker}
              className={`w-12 h-12 rounded-xl border flex items-center justify-center transition-all shrink-0 tap-highlight-transparent ${
                icon === '📌'
                  ? 'bg-white/5 border-dashed border-white/[0.15] hover:border-white/[0.25] hover:bg-white/10'
                  : 'bg-primary/10 border-primary/30 hover:bg-primary/15'
              }`}
              aria-label={t('home.habitForm.icon')}
            >
              {icon === '📌' ? (
                <span className="text-xl text-text-hint/50">+</span>
              ) : (
                <span className="text-2xl">{icon}</span>
              )}
            </button>

            {/* Emoji picker — portal to body so it escapes accordion overflow */}
            {isPickerOpen && createPortal(
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-[70]"
                  onClick={() => setPickerOpen(false)}
                  onTouchStart={() => setPickerOpen(false)}
                />
                <div
                  ref={pickerRef}
                  className="fixed z-[71] animate-fade-in"
                  style={{ top: pickerPos.top, left: pickerPos.left }}
                >
                  <Suspense fallback={
                    <div className="w-[320px] h-[400px] rounded-xl bg-surface border border-border flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                    </div>
                  }>
                    <EmojiPicker
                      onEmojiClick={handleEmojiSelect}
                      theme={'dark' as import('emoji-picker-react').Theme}
                      width={320}
                      height={400}
                      searchPlaceHolder={t('common.search')}
                      autoFocusSearch={false}
                      lazyLoadEmojis
                      skinTonesDisabled
                      previewConfig={{ showPreview: false }}
                    />
                  </Suspense>
                </div>
              </>,
              document.body,
            )}
          </div>

          {/* Name input */}
          <div className="flex-1">
            <Input
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t('home.habitForm.namePlaceholder')}
            />
          </div>
        </div>
        <div className="flex justify-end mt-1">
          <span className={`text-[10px] ${name.length > 35 ? 'text-warning' : 'text-text-hint/40'}`}>
            {name.length}/40
          </span>
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-xs font-medium text-text-hint mb-2">
          {t('home.habitForm.category')}
        </label>
        <div className="flex flex-wrap gap-2">
          {catList.map((cat) => (
            <button key={cat.id as string} type="button"
              onClick={() => onCategoryChange(cat.id as string)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                category === cat.id
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-white/5 text-text-hint border border-border hover:bg-white/10'
              }`}>
              {cat.icon as string} {(i18n.language === 'uz' ? cat.name_uz : cat.name_en) as string}
            </button>
          ))}
        </div>
        {!category && (
          <p className="text-[11px] text-text-hint/50 mt-1.5">{t('home.habitForm.categoryHint')}</p>
        )}
      </div>
    </>
  )
}
